import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Users, HeartHandshake, Gauge, FileText, Boxes, MessageCircle, Cpu, Ship } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CaseStudy from "@/components/CaseStudy";
import MarketPulse from "@/components/MarketPulse";
import EmmyAdvisor from "@/components/EmmyAdvisor";
import FieldExperience from "@/components/FieldExperience";
import Partners from "@/components/Partners";
import VerifiedDeals from "@/components/VerifiedDeals";
import { SuccessStoryStrip } from "@/components/SuccessStories";
import { ExpertCard } from "@/components/ExpertCard";
import SearchAutocomplete from "@/components/SearchAutocomplete";
import { useTranslation } from "react-i18next";

const categories = [
  { slug: "01", name: "Prelucrare primară", img: "https://images.pexels.com/photos/3637786/pexels-photo-3637786.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { slug: "02", name: "Prelucrare secundară", img: "https://images.pexels.com/photos/37162555/pexels-photo-37162555.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { slug: "20", name: "Scule și accesorii", img: "https://images.pexels.com/photos/7492884/pexels-photo-7492884.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { slug: "30", name: "Energie & încălzire", img: "https://images.pexels.com/photos/3853233/pexels-photo-3853233.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

const ecoLayers = [
  { icon: Cpu, tKey: "l1", to: "/consilier", accent: "text-amber-500" },
  { icon: BadgeCheck, tKey: "l2", to: "/experti", accent: "text-emerald-500" },
  { icon: MessageCircle, tKey: "l3", to: "/cereri", accent: "text-orange-500" },
  { icon: Ship, tKey: "l4", to: "/consilier", accent: "text-indigo-400" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [experts, setExperts] = useState([]);
  const [expertCount, setExpertCount] = useState(0);
  const { t } = useTranslation();
  useEffect(() => {
    api.get("/products").then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
    api.get("/experts").then((r) => { setExperts(r.data.slice(0, 4)); setExpertCount(r.data.length); }).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero — split screen: left value+search+live stats, right Emmy embedded above the fold */}
      <section className="relative bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1735494035457-b6e8f68b5248?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
            alt="Atelier de prelucrare a lemnului" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/90 to-slate-900/70" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 py-8 lg:py-10">
          <div className="grid lg:grid-cols-[1fr_minmax(400px,520px)] gap-8 items-start">
            {/* LEFT */}
            <div className="animate-fade-up min-w-0">
              <span className="text-xs uppercase tracking-[0.3em] font-bold text-orange-500" data-testid="hero-eyebrow">{t("hero_eyebrow")}</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mt-3 leading-[1.12] text-balance" data-testid="hero-title">
                {t("hero_title")}
              </h1>
              <p className="text-base text-slate-300 mt-4 max-w-2xl leading-relaxed" data-testid="hero-subtitle">
                {t("hero_subtitle")}
              </p>

              <div className="mt-5 max-w-xl" data-testid="hero-search-form">
                <SearchAutocomplete
                  variant="hero"
                  placeholder="Caută produse, mărci... (ex: WINTER, uscător, circular)"
                  popularTags={["WINTER", "Uscător", "Circular", "ELCON", "ROBLAND"]}
                />
              </div>

              {/* Value pillars — the 3 differentiators, above the fold */}
              <div className="flex flex-wrap gap-2.5 mt-5" data-testid="hero-pillars">
                <Link to="/vinde" className="group relative inline-flex items-center gap-2 border-2 border-orange-500 bg-orange-500/15 hover:bg-orange-500/25 px-4 py-2.5 text-sm font-bold text-white transition-colors shadow-[0_0_0_3px_rgba(234,88,12,0.15)]" data-testid="hero-pillar-eval">
                  <Gauge className="w-4 h-4 text-orange-400" /> {t("ux.hero.eval")} <ArrowRight className="w-3.5 h-3.5 text-orange-400 group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <Link to="/cereri" className="group inline-flex items-center gap-2 border border-slate-600 hover:border-orange-500 bg-slate-800/40 hover:bg-slate-800 px-4 py-2.5 text-sm font-semibold transition-colors" data-testid="hero-pillar-pdf">
                  <FileText className="w-4 h-4 text-orange-500" /> {t("ux.hero.pdf")} <ArrowRight className="w-3.5 h-3.5 text-orange-500 group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <Link to="/anunturi" className="group inline-flex items-center gap-2 border border-slate-600 hover:border-orange-500 bg-slate-800/40 hover:bg-slate-800 px-4 py-2.5 text-sm font-semibold transition-colors" data-testid="hero-pillar-market">
                  <Boxes className="w-4 h-4 text-orange-500" /> {t("ux.hero.market")} <ArrowRight className="w-3.5 h-3.5 text-orange-500 group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <Link to="/directory" className="group inline-flex items-center gap-2 border border-slate-600 hover:border-orange-500 bg-slate-800/40 hover:bg-slate-800 px-4 py-2.5 text-sm font-semibold transition-colors" data-testid="hero-pillar-guild">
                  <Users className="w-4 h-4 text-orange-500" /> {t("ux.hero.guild")} <ArrowRight className="w-3.5 h-3.5 text-orange-500 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>

              {/* LIVE verification stats strip */}
              <div className="mt-5 border-t border-slate-700/60 pt-3">
                <MarketPulse mode="strip" embedded />
              </div>
            </div>

            {/* RIGHT — Emmy embedded */}
            <div className="w-full" data-testid="hero-emmy">
              <EmmyAdvisor variant="hero" quickPrompts />
            </div>
          </div>
        </div>
      </section>

      {/* Live industry feed strip */}
      <MarketPulse mode="feed" />
      <section className="bg-slate-950 text-white border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col lg:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-slate-500">Parteneri tehnologici</span>
            {["WINTER Holztechnik", "Logica H&S", "OXO Engineering"].map((b) => (
              <span key={b} className="text-sm font-black tracking-tight text-slate-200">{b}</span>
            ))}
          </div>
          <Link to="/experti" className="inline-flex items-center gap-2.5 border border-slate-700 hover:border-orange-600 px-5 py-2.5 transition-colors group" data-testid="trust-bar-experts">
            <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" /></span>
            <span className="text-sm"><span className="font-black text-orange-500" data-testid="expert-counter">{expertCount}</span> <span className="text-slate-300">Ingineri & consultanți verificați</span></span>
            <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-orange-500 group-hover:translate-x-0.5 transition-all" />
          </Link>
        </div>
      </section>

      {/* OXO HUB Ecosystem Map — four value layers (replaces static installation block) */}
      <section className="bg-slate-950 text-white border-b border-slate-800" data-testid="eco-map">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12">
          <div className="mb-8">
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-orange-500">{t("ux.eco.eyebrow")}</span>
            <h2 className="text-2xl lg:text-3xl font-black tracking-tight mt-2 max-w-2xl">{t("ux.eco.title")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {ecoLayers.map((l, i) => (
              <Link key={l.tKey} to={l.to} className="group relative border border-slate-800 hover:border-orange-500 bg-slate-900/50 hover:bg-slate-900 p-5 flex flex-col transition-colors" data-testid={`eco-layer-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <l.icon className={`w-7 h-7 ${l.accent}`} />
                  <span className="text-[10px] font-mono text-slate-600 group-hover:text-slate-400">0{i + 1}</span>
                </div>
                <h3 className="font-bold text-white mt-4 leading-tight">{t(`ux.eco.${l.tKey}t`)}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mt-2 flex-1">{t(`ux.eco.${l.tKey}d`)}</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-orange-500 mt-4 group-hover:gap-2.5 transition-all">
                  {t("ux.eco.cta")} <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Top B2B Opportunities — LIDAR-verified, tender-ready deals */}
      <VerifiedDeals />

      {/* Árva Szakmák — social-impact highlight band */}
      <section className="bg-emerald-700 text-white" data-testid="home-arva-band">
        <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-emerald-200">
              <HeartHandshake className="w-4 h-4" /> {t("arva.eyebrow")}
            </span>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight mt-3" data-testid="home-arva-title">{t("arva.title")}</h2>
            <p className="text-emerald-50/90 mt-4 max-w-xl leading-relaxed">{t("arva.subtitle")}</p>
            <Link to="/arva-szakmak" className="inline-flex items-center gap-2 mt-7 bg-white text-emerald-800 hover:bg-emerald-50 px-7 py-3.5 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="home-arva-cta">
              {t("arva.explore")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="hidden lg:block aspect-[4/3] overflow-hidden border-4 border-emerald-600">
            <img src="https://images.pexels.com/photos/3637786/pexels-photo-3637786.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Árva Szakmák" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500" data-testid="section-categories">{t("section_categories")}</span>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight text-slate-900 mt-2">Categorii de produse</h2>
          </div>
          <Link to="/produse" className="hidden sm:inline-flex items-center gap-2 text-sm font-bold uppercase text-orange-600 hover:gap-3 transition-all">
            Toate produsele <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {categories.map((c) => (
            <Link key={c.slug} to={`/produse?cat=${c.slug}`} className="group relative aspect-[3/4] overflow-hidden border border-slate-200" data-testid={`category-${c.slug}`}>
              <img src={c.img} alt={c.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/30 to-transparent" />
              <div className="absolute bottom-0 left-0 p-5">
                <h3 className="text-white font-bold text-lg leading-tight">{c.name}</h3>
                <span className="text-orange-500 text-sm font-semibold flex items-center gap-1 mt-1 group-hover:gap-2 transition-all">Explorează <ArrowRight className="w-3.5 h-3.5" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500" data-testid="section-recommended">{t("section_recommended")}</span>
              <h2 className="text-3xl lg:text-4xl font-black tracking-tight text-slate-900 mt-2">Produse populare</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* Featured project / Case study */}
      <CaseStudy />

      {/* Árva Szakmák success stories — social proof in the sales flow */}
      <SuccessStoryStrip />

      {/* Technology partners — KATRES */}
      <Partners />

      {/* 25+ ani field experience */}
      <FieldExperience />

      {/* Featured experts spotlight */}
      <section className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-orange-500 flex items-center gap-2" data-testid="section-engineering"><Users className="w-4 h-4" /> {t("section_engineering")}</span>
              <h2 className="text-3xl lg:text-4xl font-black tracking-tight mt-2">Experți de top în reflectoare</h2>
              <p className="text-slate-400 mt-3 max-w-xl">Ingineri și consultanți verificați pentru automatizări, calibrare uscătoare și logistică biomasă.</p>
            </div>
            <Link to="/experti" className="hidden sm:inline-flex items-center gap-2 text-sm font-bold uppercase text-orange-500 hover:gap-3 transition-all">
              Tot directorul <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {experts.length === 0 ? (
            <div className="border border-slate-800 p-12 text-center">
              <BadgeCheck className="w-10 h-10 mx-auto text-slate-600" />
              <p className="text-slate-400 mt-4">Primii experți se alătură în curând.</p>
              <Link to="/cont-expert" className="inline-block mt-4 text-orange-500 font-semibold hover:underline">Înscrie-te ca expert →</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {experts.map((e) => <ExpertCard key={e.id} expert={e} />)}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="bg-slate-900 text-white p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight">Ai nevoie de consultanță tehnică?</h2>
            <p className="text-slate-400 mt-3 max-w-xl">Echipa OXO și rețeaua de ingineri verificați te ajută să alegi echipamentul potrivit și să structurezi tranzacția.</p>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 px-8 py-4 font-bold uppercase tracking-wide text-sm transition-colors shrink-0" data-testid="cta-contact">
            Contactează-ne <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
